import { useEffect } from 'react';
import Navbar from '../../components/Navbar/Navbar';
import Footer from '../../components/Footer/Footer';
import { RefreshCw, Mail } from 'lucide-react';
import '../Policy.css';

export default function Refund() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="policy-page">
      <Navbar />
      <main className="policy-page__main">
        <header className="policy-header">
          <div className="policy-kicker">Billing & Refunds</div>
          <h1 className="policy-title">Refund Policy</h1>
          <p className="policy-subtitle">
            Clear guidelines on subscription cancellations, money-back guarantees, and Paddle billing management.
          </p>
          <div className="policy-meta">
            <RefreshCw size={14} />
            <span>Effective Date: August 25, 2026</span>
          </div>
        </header>

        <div className="policy-card">
          <section className="policy-section">
            <h2>1. Merchant of Record</h2>
            <p>
              All Revitrone subscription orders, transactions, and refunds are managed and processed by our reseller and Merchant of Record, <strong>Paddle.com Market Ltd ("Paddle")</strong>. Paddle handles order verification, tax administration, localized currencies, and customer billing support.
            </p>
          </section>

          <section className="policy-section">
            <h2>2. 14-Day Money-Back Guarantee</h2>
            <p>
              We want you to be completely satisfied with Revitrone's AI design tools. If you purchase a paid subscription (Pro or Studio plan) for the first time, you are eligible for a <strong>100% refund within 14 days</strong> of your initial purchase, subject to the following fair-use conditions:
            </p>
            <ul>
              <li><strong>Fair Usage Limit:</strong> The refund guarantee applies if you have consumed <strong>less than 20%</strong> of your plan's monthly proxy token allocation during the 14-day period.</li>
              <li><strong>First Purchase Only:</strong> The 14-day money-back guarantee applies to your initial subscription purchase and does not apply to subsequent renewal billing cycles.</li>
            </ul>
          </section>

          <div className="policy-callout">
            <p>
              <strong>Zero-Cost BYOK Plan:</strong> The Basic plan is free of charge ($0/mo) and uses your own API keys. Because no charges occur for the Basic tier, refund requests are not applicable.
            </p>
          </div>

          <section className="policy-section">
            <h2>3. Subscription Cancellation</h2>
            <p>
              You can cancel your active subscription at any time without penalty:
            </p>
            <ul>
              <li><strong>Self-Service Cancellation:</strong> Navigate to your <strong>Dashboard &gt; Settings</strong> or access your billing link via your Paddle receipt email to open the customer portal.</li>
              <li><strong>Access Until End of Period:</strong> When you cancel, your plan remains active until the final day of your current billing cycle. You will retain full access to your token balance and features until that date.</li>
              <li><strong>No Automatic Next Cycle Charge:</strong> Once cancelled, your credit card will not be charged for subsequent billing periods.</li>
            </ul>
          </section>

          <section className="policy-section">
            <h2>4. Non-Refundable Scenarios</h2>
            <p>Refunds will generally not be granted in the following cases:</p>
            <ul>
              <li>More than 14 days have passed since the original transaction date.</li>
              <li>More than 20% of monthly proxy token credits have been utilized.</li>
              <li>Account termination resulting from a breach of our Terms of Service (e.g., credential abuse or reverse engineering).</li>
              <li>Automatic recurring renewal charges where cancellation was requested after the renewal billing date (except where required by applicable local law such as EU statutory withdrawal rights).</li>
            </ul>
          </section>

          <section className="policy-section">
            <h2>5. How to Request a Refund</h2>
            <p>To request a refund under our 14-day policy, please follow these steps:</p>
            <ol style={{ paddingLeft: '24px', color: '#525252', lineHeight: '1.7' }}>
              <li style={{ marginBottom: '8px' }}>Gather your account email address and your Paddle Transaction / Order ID (found on your email receipt).</li>
              <li style={{ marginBottom: '8px' }}>Send an email to <a href="mailto:revitroneapp@gmail.com" style={{ color: '#171717', fontWeight: 600 }}>revitroneapp@gmail.com</a> with the subject line <code>Refund Request - [Order ID]</code>.</li>
              <li style={{ marginBottom: '8px' }}>Our support team will review your usage metrics and process your refund within 1–2 business days.</li>
            </ol>
            <p style={{ marginTop: '16px' }}>
              Approved refunds are credited back to your original payment method via Paddle within 5 to 10 business days depending on your bank or financial institution.
            </p>
          </section>

          <div className="policy-contact-box">
            <h3>Need Assistance with Your Billing?</h3>
            <p>Have questions about an invoice, transaction, or subscription upgrade? Contact our billing support.</p>
            <a href="mailto:revitroneapp@gmail.com" className="policy-contact-btn">
              <Mail size={16} /> Contact Billing Support
            </a>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
